import React, { useCallback, useEffect, useState } from 'react';
import {
  Search, AlertTriangle, Loader2, TrendingDown, TrendingUp, Sparkles, Share2, Copy, Check,
} from 'lucide-react';
import { brandingRootStyle, brandingProductName } from '../lib/embedBranding';
import { useEmbedToken } from '../lib/embedTokenBridge';
import { emit } from '../lib/embedChannel';

/**
 * Embed Investigate Why (iframe surface).
 *
 * Mounted at `/embed/v2/investigate?token=<jwt>`.
 *
 * The host user types the metric that changed (e.g. "Revenue fell 15%
 * Mon-Wed"), optionally attaches a short note, and INZARA returns a
 * ranked list of hypotheses — dimension + value + delta% + confidence
 * + one-line explanation — plus a natural-language summary. Backed by
 * the workspace's semantic entities so hypotheses reference real
 * dimensions, not hallucinated ones.
 *
 * Runs are persisted server-side so the host can share a `run_id`
 * link and land users directly on the result page.
 */

const API = process.env.REACT_APP_BACKEND_URL || '';

async function authenticate(token) {
  const res = await fetch(`${API}/api/embed/v2/authenticate`, {
    method: 'POST',
    headers: { 'Authorization': `Bearer ${token}` },
  });
  if (!res.ok) throw new Error(`authenticate ${res.status}`);
  return res.json();
}

async function runInvestigation(token, metric, note) {
  const res = await fetch(`${API}/api/embed/v2/investigate/run`, {
    method: 'POST',
    headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
    body: JSON.stringify({ metric, note: note || null }),
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    const detail = body?.detail || {};
    const err = new Error(detail?.message || `investigate ${res.status}`);
    err.status = res.status;
    err.code = detail?.code;
    throw err;
  }
  return res.json();
}

function HypothesisCard({ h, index }) {
  const delta = h.delta_pct;
  const negative = delta !== null && delta !== undefined && delta < 0;
  const Icon = negative ? TrendingDown : TrendingUp;
  const confPct = h.confidence != null ? Math.round(h.confidence * 100) : null;
  return (
    <div
      className="rounded-lg border border-border/40 bg-background/60 p-3 space-y-1.5"
      data-testid={`embed-investigate-hypothesis-${index}`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <div className="text-[10px] uppercase tracking-wider text-muted-foreground">
            {h.dimension}
          </div>
          <div className="text-sm font-semibold truncate" data-testid={`embed-investigate-hypothesis-${index}-value`}>
            {h.value}
          </div>
        </div>
        {delta != null && (
          <div className={`flex items-center gap-1 text-[11px] font-semibold px-2 py-1 rounded-md ${
            negative ? 'bg-rose-500/15 text-rose-300' : 'bg-emerald-500/15 text-emerald-300'
          }`}>
            <Icon className="w-3 h-3" />
            {delta > 0 ? '+' : ''}{delta.toFixed(1)}%
          </div>
        )}
      </div>
      {h.explanation && (
        <div className="text-[11px] text-muted-foreground leading-relaxed">
          {h.explanation}
        </div>
      )}
      {confPct !== null && (
        <div className="flex items-center gap-2 pt-1">
          <div className="text-[9px] uppercase tracking-wider text-muted-foreground/70">
            Confidence
          </div>
          <div className="flex-1 h-1 rounded-full bg-muted/40 overflow-hidden">
            <div
              className="h-full bg-indigo-400"
              style={{ width: `${confPct}%` }}
            />
          </div>
          <div className="text-[10px] font-mono text-muted-foreground">
            {confPct}%
          </div>
        </div>
      )}
    </div>
  );
}

export default function EmbedInvestigatePage() {
  const token = useEmbedToken();
  const [claims, setClaims] = useState(null);
  const [branding, setBranding] = useState(null);
  const [metric, setMetric] = useState('');
  const [note, setNote] = useState('');
  const [pending, setPending] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [shareOpen, setShareOpen] = useState(false);
  const [shareTtl, setShareTtl] = useState(7);
  const [sharePassword, setSharePassword] = useState('');
  const [shareSlack, setShareSlack] = useState('');
  const [creatingShare, setCreatingShare] = useState(false);
  const [share, setShare] = useState(null);
  const [shareStats, setShareStats] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    authenticate(token)
      .then((c) => {
        if (cancelled) return;
        setClaims(c);
        setBranding(c?.branding || null);
        emit('viewer.identified', { sub: c?.sub, roles: c?.roles || [], capabilities: c?.capabilities || [] });
      })
      .catch((e) => { if (!cancelled) setError(e.message); });
    return () => { cancelled = true; };
  }, [token]);

  // Poll the share's view analytics every 15s while the panel is
  // open — sharer sees real-time engagement without a refresh.
  useEffect(() => {
    if (!share?.share_id || !token || !result?.run_id) return undefined;
    let cancelled = false;
    const poll = async () => {
      try {
        const r = await fetch(
          `${API}/api/embed/v2/investigate/runs/${encodeURIComponent(result.run_id)}/shares`,
          { headers: { 'Authorization': `Bearer ${token}` } },
        );
        if (!r.ok) return;
        const b = await r.json();
        if (cancelled) return;
        const mine = (b?.data || []).find((s) => s.share_id === share.share_id);
        if (mine) {
          setShareStats({
            view_count: mine.view_count || 0,
            last_viewed_at: mine.last_viewed_at || null,
          });
        }
      } catch { /* silent */ }
    };
    poll();
    const iv = setInterval(poll, 15000);
    return () => { cancelled = true; clearInterval(iv); };
  }, [share?.share_id, token, result?.run_id]);

  const submit = useCallback(async () => {
    const m = metric.trim();
    if (!m || pending || !token) return;
    setPending(true);
    setError(null);
    setResult(null);
    setShare(null);
    setShareStats(null);
    setShareOpen(false);
    try {
      const r = await runInvestigation(token, m, note.trim() || null);
      setResult(r);
      emit('investigate.run', { run_id: r.run_id, metric: r.metric });
    } catch (e) {
      setError(e.message);
      if (e.code === 'budget_exhausted') {
        emit('budget.exhausted', { kind: 'ai', message: e.message });
      } else {
        emit('error', { code: e.code || 'unknown', message: e.message });
      }
    } finally {
      setPending(false);
    }
  }, [metric, note, pending, token]);

  const canWrite = (claims?.capabilities || []).includes('write');
  const createShare = useCallback(async () => {
    if (!result?.run_id || !token) return;
    setCreatingShare(true);
    try {
      const trimmedSlack = shareSlack.trim();
      const res = await fetch(
        `${API}/api/embed/v2/investigate/runs/${encodeURIComponent(result.run_id)}/share`,
        {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({
            ttl_days: Math.max(1, Math.min(30, Number(shareTtl) || 7)),
            password: sharePassword.trim() || null,
            slack_webhook_url: trimmedSlack || null,
          }),
        },
      );
      if (!res.ok) {
        const b = await res.json().catch(() => ({}));
        throw new Error(b?.detail?.message || `share ${res.status}`);
      }
      const body = await res.json();
      const url = `${window.location.origin}/investigations/shared/${body.share_id}`;
      setShare({ ...body, share_url: url });
      setShareStats({ view_count: 0, last_viewed_at: null });
      emit('investigate.share.created', { share_id: body.share_id, run_id: result.run_id });
    } catch (e) {
      setError(e.message);
    } finally {
      setCreatingShare(false);
    }
  }, [result?.run_id, token, shareTtl, sharePassword, shareSlack]);

  const copyShareUrl = useCallback(async () => {
    if (!share?.share_url) return;
    try {
      await navigator.clipboard.writeText(share.share_url);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch { /* clipboard denied */ }
  }, [share?.share_url]);

  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center text-sm text-muted-foreground">
        Waiting for session token…
      </div>
    );
  }

  const productName = brandingProductName(branding);

  return (
    <div
      className="min-h-screen bg-background text-foreground p-4"
      style={brandingRootStyle(branding)}
      data-testid="embed-investigate-root"
    >
      <header className="mb-4 flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <Search className="w-4 h-4 text-indigo-300 shrink-0" />
            <div className="text-sm font-semibold" data-testid="embed-investigate-product-name">
              {productName || 'Investigate Why'}
            </div>
          </div>
          <div className="text-[10px] text-muted-foreground mt-0.5">
            Describe a metric that changed and get ranked hypotheses.
          </div>
        </div>
        {claims && (
          <div className="text-[10px] text-muted-foreground text-right shrink-0" data-testid="embed-investigate-viewer">
            <div className="font-mono">{claims.sub}</div>
          </div>
        )}
      </header>

      <div className="space-y-3 mb-4">
        <div>
          <label className="block text-[10px] uppercase tracking-wider text-muted-foreground mb-1">
            Metric
          </label>
          <input
            type="text"
            value={metric}
            onChange={(e) => setMetric(e.target.value)}
            placeholder="e.g. Revenue fell 15% Mon-Wed"
            maxLength={120}
            className="w-full bg-transparent border border-border/40 rounded-md px-2.5 py-1.5 text-[12px] outline-none focus:border-indigo-400/60"
            data-testid="embed-investigate-metric-input"
          />
        </div>
        <div>
          <label className="block text-[10px] uppercase tracking-wider text-muted-foreground mb-1">
            Note <span className="normal-case text-muted-foreground/80">(optional context)</span>
          </label>
          <textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Any context — timeframe, affected segment, hypothesis…"
            rows={2}
            maxLength={500}
            className="w-full bg-transparent border border-border/40 rounded-md px-2.5 py-1.5 text-[12px] outline-none focus:border-indigo-400/60 resize-none"
            data-testid="embed-investigate-note-input"
          />
        </div>
        <button
          type="button"
          onClick={submit}
          disabled={!metric.trim() || pending}
          className="inline-flex items-center gap-1.5 rounded-md bg-indigo-500 px-4 py-2 text-[12px] font-medium text-white hover:bg-indigo-400 disabled:opacity-40 disabled:cursor-not-allowed"
          data-testid="embed-investigate-run"
        >
          {pending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
          {pending ? 'Investigating…' : 'Investigate'}
        </button>
      </div>

      {error && (
        <div className="rounded-md border border-rose-500/40 bg-rose-500/10 px-3 py-2 text-[11px] text-rose-200 flex items-start gap-2 mb-3" data-testid="embed-investigate-error">
          <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
          <div>{error}</div>
        </div>
      )}

      {result && (
        <div className="space-y-3" data-testid="embed-investigate-result">
          <div className="rounded-lg border border-border/40 bg-muted/10 p-3">
            <div className="flex items-center justify-between mb-1">
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground">
                Summary
              </div>
              {canWrite && (
                <button
                  type="button"
                  onClick={() => setShareOpen((o) => !o)}
                  className="inline-flex items-center gap-1 rounded-md border border-indigo-500/40 bg-indigo-500/[0.08] hover:bg-indigo-500/[0.16] px-2 py-1 text-[10px] text-indigo-200 transition-colors"
                  data-testid="embed-investigate-share-toggle"
                >
                  <Share2 className="w-3 h-3" />
                  {share ? 'Share link' : 'Share'}
                </button>
              )}
            </div>
            <div className="text-[12px] leading-relaxed" data-testid="embed-investigate-summary">
              {result.summary || 'No summary available.'}
            </div>
          </div>

          {shareOpen && (
            <div className="rounded-lg border border-indigo-500/40 bg-indigo-500/[0.06] p-3 space-y-2" data-testid="embed-investigate-share-panel">
              {!share ? (
                <>
                  <div className="text-[11px] font-semibold text-indigo-100">
                    Create a public share link
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="block text-[9px] uppercase tracking-wider text-muted-foreground mb-1">
                        Expires in (days)
                      </label>
                      <input
                        type="number"
                        min={1}
                        max={30}
                        value={shareTtl}
                        onChange={(e) => setShareTtl(e.target.value)}
                        className="w-full bg-transparent border border-border/40 rounded-md px-2 py-1 text-[11px] outline-none focus:border-indigo-400/60"
                        data-testid="embed-investigate-share-ttl"
                      />
                    </div>
                    <div>
                      <label className="block text-[9px] uppercase tracking-wider text-muted-foreground mb-1">
                        Password (optional)
                      </label>
                      <input
                        type="password"
                        value={sharePassword}
                        onChange={(e) => setSharePassword(e.target.value)}
                        placeholder="Leave blank for public"
                        maxLength={64}
                        className="w-full bg-transparent border border-border/40 rounded-md px-2 py-1 text-[11px] outline-none focus:border-indigo-400/60"
                        data-testid="embed-investigate-share-password"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-[9px] uppercase tracking-wider text-muted-foreground mb-1">
                      Slack webhook <span className="normal-case text-muted-foreground/80">(optional — DM on first view)</span>
                    </label>
                    <input
                      type="text"
                      value={shareSlack}
                      onChange={(e) => setShareSlack(e.target.value)}
                      placeholder="https://hooks.slack.com/services/..."
                      maxLength={250}
                      className="w-full bg-transparent border border-border/40 rounded-md px-2 py-1 text-[11px] outline-none focus:border-indigo-400/60 font-mono"
                      data-testid="embed-investigate-share-slack"
                    />
                  </div>
                  <button
                    type="button"
                    onClick={createShare}
                    disabled={creatingShare}
                    className="rounded-md bg-indigo-500 px-3 py-1.5 text-[11px] font-medium text-white hover:bg-indigo-400 disabled:opacity-40"
                    data-testid="embed-investigate-share-create"
                  >
                    {creatingShare ? 'Creating…' : 'Generate link'}
                  </button>
                </>
              ) : (
                <>
                  <div className="text-[11px] font-semibold text-indigo-100">
                    Share link generated
                  </div>
                  <div className="flex items-center gap-2">
                    <input
                      type="text"
                      readOnly
                      value={share.share_url}
                      className="flex-1 bg-background/60 border border-border/40 rounded-md px-2 py-1 text-[11px] font-mono"
                      data-testid="embed-investigate-share-url"
                      onClick={(e) => e.target.select()}
                    />
                    <button
                      type="button"
                      onClick={copyShareUrl}
                      className="inline-flex items-center gap-1 rounded-md border border-indigo-500/40 bg-indigo-500/[0.12] hover:bg-indigo-500/[0.24] px-2 py-1 text-[10px] text-indigo-100"
                      data-testid="embed-investigate-share-copy"
                    >
                      {copied ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
                      {copied ? 'Copied' : 'Copy'}
                    </button>
                  </div>
                  <div className="text-[10px] text-muted-foreground">
                    Expires {share.expires_at ? new Date(share.expires_at).toLocaleString() : '—'}
                    {share.has_password && ' · password protected'}
                    {share.has_slack_webhook && ' · Slack alert on first view'}
                  </div>
                  {shareStats && (
                    <div className="mt-2 flex items-center gap-3 rounded-md bg-background/40 border border-border/30 px-3 py-2 text-[10px]" data-testid="embed-investigate-share-stats">
                      <span className="text-indigo-200 font-semibold">
                        {shareStats.view_count ?? 0} view{(shareStats.view_count ?? 0) === 1 ? '' : 's'}
                      </span>
                      {shareStats.last_viewed_at ? (
                        <span className="text-muted-foreground">
                          Last opened {new Date(shareStats.last_viewed_at).toLocaleString()}
                        </span>
                      ) : (
                        <span className="text-muted-foreground italic">Not opened yet</span>
                      )}
                    </div>
                  )}
                  <ShareManagementPanel
                    shareId={share.share_id}
                    token={token}
                    onRotated={(hasPw) => setShare((s) => ({ ...s, has_password: hasPw }))}
                  />
                </>
              )}
            </div>
          )}

          <div>
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1.5">
              Hypotheses ({(result.hypotheses || []).length})
            </div>
            {(!result.hypotheses || result.hypotheses.length === 0) ? (
              <div className="text-[11px] italic text-muted-foreground">
                No hypotheses returned — try adding more context in the note.
              </div>
            ) : (
              <div className="space-y-2">
                {result.hypotheses.map((h, i) => (
                  <HypothesisCard key={`${h.dimension}-${h.value}-${i}`} h={h} index={i} />
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}


/**
 * Post-share management strip: password rotation + access log peek.
 * Kept in a tight collapsible block so it doesn't dominate the
 * results view when the sharer just wants the URL.
 */
function ShareManagementPanel({ shareId, token, onRotated }) {
  const [open, setOpen] = React.useState(false);
  const [newPw, setNewPw] = React.useState('');
  const [rotating, setRotating] = React.useState(false);
  const [rotateMsg, setRotateMsg] = React.useState(null);
  const [log, setLog] = React.useState(null);
  const [logLoading, setLogLoading] = React.useState(false);
  const [geoSummary, setGeoSummary] = React.useState(null);

  const rotate = async (clear) => {
    if (!shareId || !token) return;
    setRotating(true);
    setRotateMsg(null);
    try {
      const res = await fetch(
        `${API}/api/embed/v2/investigate/shares/${encodeURIComponent(shareId)}/rotate-password`,
        {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({ password: clear ? null : newPw.trim() || null }),
        },
      );
      if (!res.ok) {
        const b = await res.json().catch(() => ({}));
        throw new Error(b?.detail?.message || `rotate ${res.status}`);
      }
      const b = await res.json();
      setRotateMsg(clear ? 'Password cleared — link is now open.' : 'Password rotated. Share the new one out-of-band.');
      setNewPw('');
      onRotated?.(b.has_password);
    } catch (e) {
      setRotateMsg(`Failed: ${e.message}`);
    } finally {
      setRotating(false);
    }
  };

  const loadLog = async () => {
    if (!shareId || !token) return;
    setLogLoading(true);
    try {
      const res = await fetch(
        `${API}/api/embed/v2/investigate/shares/${encodeURIComponent(shareId)}/access-log`,
        { headers: { 'Authorization': `Bearer ${token}` } },
      );
      if (!res.ok) throw new Error(`access-log ${res.status}`);
      const b = await res.json();
      setLog(b?.data || []);
    } catch (e) {
      setLog([]);
    } finally {
      setLogLoading(false);
    }
    // Refresh the geo summary in parallel — best effort; failures leave the
    // strip in its previous state so the primary log view stays functional.
    try {
      const gr = await fetch(
        `${API}/api/embed/v2/investigate/shares/${encodeURIComponent(shareId)}/access-log/summary`,
        { headers: { 'Authorization': `Bearer ${token}` } },
      );
      if (gr.ok) setGeoSummary(await gr.json());
    } catch (e) { /* silent */ }
  };

  const downloadCsv = async () => {
    if (!shareId || !token) return;
    try {
      const res = await fetch(
        `${API}/api/embed/v2/investigate/shares/${encodeURIComponent(shareId)}/access-log.csv`,
        { headers: { 'Authorization': `Bearer ${token}` } },
      );
      if (!res.ok) throw new Error(`csv ${res.status}`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `access-log-${shareId}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) { /* silent — bar just doesn't download */ }
  };

  return (
    <div className="mt-2" data-testid="embed-investigate-share-mgmt">
      <button
        type="button"
        onClick={() => { const next = !open; setOpen(next); if (next && log === null) loadLog(); }}
        className="text-[10px] text-indigo-300 hover:text-indigo-100 underline underline-offset-2"
        data-testid="embed-investigate-share-mgmt-toggle"
      >
        {open ? 'Hide' : 'Rotate password · View access log'}
      </button>
      {open && (
        <div className="mt-2 space-y-3 rounded-md border border-border/30 bg-background/30 p-3">
          {/* Password rotation */}
          <div data-testid="embed-investigate-share-rotate-panel">
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1.5">
              Rotate password
            </div>
            <div className="flex items-center gap-2">
              <input
                type="password"
                value={newPw}
                onChange={(e) => setNewPw(e.target.value)}
                placeholder="New password"
                maxLength={64}
                className="flex-1 bg-transparent border border-border/40 rounded-md px-2 py-1 text-[11px] outline-none focus:border-indigo-400/60"
                data-testid="embed-investigate-share-rotate-input"
              />
              <button
                type="button"
                onClick={() => rotate(false)}
                disabled={rotating || !newPw.trim()}
                className="rounded-md bg-indigo-500 px-2 py-1 text-[10px] font-medium text-white hover:bg-indigo-400 disabled:opacity-40"
                data-testid="embed-investigate-share-rotate-apply"
              >
                {rotating ? '…' : 'Rotate'}
              </button>
              <button
                type="button"
                onClick={() => rotate(true)}
                disabled={rotating}
                className="rounded-md border border-border/40 px-2 py-1 text-[10px] hover:bg-muted/40 disabled:opacity-40"
                data-testid="embed-investigate-share-rotate-clear"
              >
                Clear
              </button>
            </div>
            {rotateMsg && (
              <div className="mt-1.5 text-[10px] text-muted-foreground" data-testid="embed-investigate-share-rotate-msg">
                {rotateMsg}
              </div>
            )}
          </div>

          {/* Access log */}
          <div data-testid="embed-investigate-share-log-panel">
            <div className="flex items-center justify-between mb-1.5">
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground">
                Access log
              </div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={downloadCsv}
                  className="text-[9px] text-emerald-300 hover:text-emerald-100"
                  data-testid="embed-investigate-share-log-csv"
                  title="Export the access log as CSV"
                >
                  CSV
                </button>
                <button
                  type="button"
                  onClick={loadLog}
                  className="text-[9px] text-indigo-300 hover:text-indigo-100"
                  data-testid="embed-investigate-share-log-refresh"
                >
                  Refresh
                </button>
              </div>
            </div>
            {geoSummary && geoSummary.by_country && geoSummary.by_country.length > 0 && (
              <div className="mb-1.5 flex flex-wrap gap-1" data-testid="embed-investigate-share-log-geo">
                {geoSummary.by_country.slice(0, 8).map((g) => (
                  <span
                    key={g.country}
                    className="inline-flex items-center gap-1 rounded-full border border-indigo-500/40 bg-indigo-500/[0.10] px-2 py-0.5 text-[9px] font-mono text-indigo-100"
                    data-testid={`embed-investigate-share-log-geo-${g.country}`}
                  >
                    {g.country} · {g.count}
                  </span>
                ))}
                {geoSummary.unknown_country_views > 0 && (
                  <span className="inline-flex items-center gap-1 rounded-full border border-border/40 bg-background/40 px-2 py-0.5 text-[9px] text-muted-foreground">
                    ?? · {geoSummary.unknown_country_views}
                  </span>
                )}
              </div>
            )}
            {logLoading ? (
              <div className="text-[10px] text-muted-foreground italic">Loading…</div>
            ) : !log || log.length === 0 ? (
              <div className="text-[10px] text-muted-foreground italic" data-testid="embed-investigate-share-log-empty">
                No views yet.
              </div>
            ) : (
              <ul className="space-y-1 max-h-40 overflow-auto">
                {log.map((entry, i) => (
                  <li
                    key={`${entry.ts}-${i}`}
                    className="flex items-center justify-between gap-2 text-[10px] rounded border border-border/30 bg-background/40 px-2 py-1"
                    data-testid={`embed-investigate-share-log-row-${i}`}
                  >
                    <div className="min-w-0 flex-1">
                      <div className="font-mono text-muted-foreground truncate">
                        {entry.ip_hash ? `#${entry.ip_hash}` : 'anon'}
                        {entry.country && (
                          <span className="ml-1.5 inline-block px-1 py-[1px] rounded bg-indigo-500/20 text-indigo-200 font-sans text-[9px] font-semibold">
                            {entry.country}
                          </span>
                        )}
                      </div>
                      {entry.user_agent && (
                        <div className="text-[9px] text-muted-foreground/70 truncate">
                          {entry.user_agent}
                        </div>
                      )}
                    </div>
                    <div className="shrink-0 text-[9px] text-muted-foreground/80">
                      {new Date(entry.ts).toLocaleString()}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
